import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Phone, Pill, TestTube, Video, Home } from 'lucide-react';

const NumberedSection = ({ number, title, description, features, image, visual, imageVariant = "framed", reverse = false }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const renderVisual = () => {
    // Phone-frame image variant
    if (image) {
      if (imageVariant === "standalone") {
        return (
          <div className="relative mx-auto w-full max-w-[420px] px-4">
            <div className="absolute -inset-10 bg-gradient-to-br from-teal-500/25 via-cyan-500/10 to-transparent rounded-[3rem] blur-3xl" />
            <img
              src={image}
              alt={`${title} preview`}
              className="relative w-full h-auto object-contain drop-shadow-[0_30px_80px_rgba(20,184,166,0.22)]"
              data-testid={`section-image-${number}`}
            />
            <div className="absolute bottom-6 right-0 bg-teal-500 text-slate-950 text-xs font-bold px-4 py-2 rounded-full shadow-lg shadow-teal-500/30 rotate-3">
              Live in-app
            </div>
          </div>
        );
      }

      return (
        <div className="relative mx-auto w-full max-w-[340px]">
          {/* Ambient glow */}
          <div className="absolute -inset-8 bg-gradient-to-br from-teal-500/30 via-cyan-500/10 to-transparent rounded-[3rem] blur-3xl" />
          {/* Phone frame */}
          <div className="relative rounded-[2.5rem] bg-gradient-to-br from-slate-800 to-slate-900 p-3 shadow-2xl border border-white/10">
            <div className="rounded-[2rem] overflow-hidden bg-black">
              <img
                src={image}
                alt={`${title} preview`}
                className="w-full h-auto object-cover"
                data-testid={`section-image-${number}`}
              />
            </div>
          </div>
          {/* Floating badge */}
          <div className="absolute -bottom-4 -right-4 bg-teal-500 text-slate-950 text-xs font-bold px-4 py-2 rounded-full shadow-lg shadow-teal-500/30 rotate-3">
            Live in-app
          </div>
        </div>
      );
    }

    // Custom "Integrated Services" icon grid visual
    if (visual === "services") {
      const services = [
        { icon: Pill, label: "Pharmacy", desc: "Doorstep delivery" },
        { icon: TestTube, label: "Diagnostics", desc: "At-home labs" },
        { icon: Video, label: "Consults", desc: "Specialists on call" },
        { icon: Home, label: "Home Visits", desc: "Care at your door" },
      ];
      return (
        <div className="relative mx-auto w-full max-w-md">
          <div className="absolute -inset-8 bg-gradient-to-br from-teal-500/20 via-cyan-500/10 to-transparent rounded-[3rem] blur-3xl" />
          <div className="relative grid grid-cols-2 gap-4">
            {services.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
                className="group relative p-6 rounded-2xl bg-slate-900/60 backdrop-blur-md border border-white/10 hover:border-teal-500/40 transition-colors"
                data-testid={`service-card-${s.label.toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <s.icon className="h-6 w-6 text-teal-300" />
                </div>
                <div className="text-lg font-semibold text-white">{s.label}</div>
                <div className="text-sm text-slate-400 mt-1">{s.desc}</div>
                <div className="absolute top-4 right-4 text-xs text-teal-400/60 font-mono">0{i + 1}</div>
              </motion.div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <motion.section
      ref={ref}
      className="py-32 px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className={`max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center ${
        reverse ? 'lg:flex-row-reverse' : ''
      }`}>
        <motion.div
          variants={itemVariants}
          className={`space-y-8 ${reverse ? 'lg:order-2' : ''}`}
        >
          {/* Number */}
          <div className="relative">
            <span className="text-9xl font-bold text-teal-500/10 absolute -top-8 -left-4">
              {number}
            </span>
            <h2 className="text-5xl md:text-6xl font-bold text-white relative z-10">
              {title}
            </h2>

            {/* WhatsApp Contact for WhatsApp Integration section */}
            {title === "WhatsApp Integration" && (
              <div className="mt-6 inline-flex items-center gap-3 bg-teal-500/10 border border-teal-500/30 rounded-full px-6 py-3 hover:bg-teal-500/20 transition-all duration-300 group cursor-pointer">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <a
                  href="https://wa.me/918448820700"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-teal-400 group-hover:text-teal-300 transition-colors"
                  data-testid="whatsapp-contact-link"
                >
                  +91 8448820700
                </a>
              </div>
            )}
          </div>

          <p className="text-xl text-slate-300 leading-relaxed">
            {description}
          </p>

          {features && (
            <ul className="space-y-4">
              {features.map((feature, index) => (
                <motion.li
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-3 text-slate-300"
                >
                  <span className="w-2 h-2 bg-teal-400 rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-lg">{feature}</span>
                </motion.li>
              ))}
            </ul>
          )}
        </motion.div>

        <motion.div
          variants={itemVariants}
          className={`${reverse ? 'lg:order-1' : ''}`}
        >
          {renderVisual()}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default NumberedSection;
